import { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Popover from '@/shared/ui/popover/Popover';
import type { ISidebarMenuItemProps } from '../types/sidebar.types';

/**
 * Individual sidebar menu item component
 * Handles rendering of menu items with proper styling and interactions
 */
const SidebarMenuItem = memo<ISidebarMenuItemProps>(({
  item,
  isExpanded,
  isActive,
  onClick,
  onMouseEnter,
}) => {
  const { t } = useTranslation();

  /**
   * Handle click event
   */
  const handleClick = () => {
    onClick(item);
  };

  /**
   * Handle mouse enter event
   */
  const handleMouseEnter = () => {
    onMouseEnter?.();
  };

  /**
   * Render expanded menu item with icon and text
   */
  const renderExpandedItem = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', minWidth: 0 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        flexShrink: 0,
        fontSize: '16px',
        color: isActive ? '#FFFFFF' : '#374151'
      }}>
        {item.icon}
      </Box>
      <Typography sx={{
        fontSize: '14px',
        fontWeight: isActive ? 600 : 500,
        color: isActive ? '#FFFFFF' : '#374151',
        flex: 1,
        textAlign: 'left'
      }}>
        {t(`SIDEBAR.${item.translate}`)}
      </Typography>
    </Box>
  );

  /**
   * Render collapsed menu item with popover – tartibli icon qutisi va active ko‘rinishi
   */
  const renderCollapsedItem = () => (
    <Popover
      placement="right"
      content={
        <Box sx={{
          width: 180,
          padding: '12px 14px',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          border: '1px solid #E2E8F0',
        }}>
          <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
            {t(`SIDEBAR.${item.translate}`)}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: '12px',
          fontSize: '18px',
          color: isActive ? '#FFFFFF' : '#475569',
          background: isActive
            ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
            : 'transparent',
          boxShadow: isActive ? '0 4px 12px rgba(59, 130, 246, 0.35)' : 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: isActive
              ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
              : '#F1F5F9',
            color: isActive ? '#FFFFFF' : '#1E293B',
          },
        }}
      >
        {item.icon}
      </Box>
    </Popover>
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isExpanded ? 'flex-start' : 'center',
        gap: '14px',
        padding: isExpanded ? '8px 12px' : '6px 0',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        fontSize: '15px',
        fontWeight: isActive ? 700 : 500,
        color: isActive ? '#FFFFFF' : '#374151',
        cursor: 'pointer',
        borderRadius: '12px',
        background: isExpanded && isActive
          ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
          : !isExpanded && isActive
            ? 'transparent'
            : 'transparent',
        margin: isExpanded ? 0 : 0,
        position: 'relative',
        boxShadow: isExpanded && isActive
          ? '0 8px 32px rgba(59, 130, 246, 0.3)'
          : 'none',
        '&:hover': isExpanded ? {
          background: isActive
            ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
            : 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
          color: isActive ? '#FFFFFF' : '#1E293B',
          transform: isActive ? 'translateY(-1px)' : 'translateX(4px)',
          boxShadow: isActive
            ? '0 12px 40px rgba(59, 130, 246, 0.4)'
            : '0 4px 16px rgba(0, 0, 0, 0.1)',
        } : {},
        '&::before': isActive && isExpanded ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '4px',
          height: '24px',
          background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 100%)',
          borderRadius: '0 2px 2px 0',
          boxShadow: '2px 0 8px rgba(255, 255, 255, 0.3)',
        } : isActive && !isExpanded ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '3px',
          height: '20px',
          background: 'linear-gradient(180deg, #3B82F6 0%, #1D4ED8 100%)',
          borderRadius: '0 2px 2px 0',
        } : {},
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      {isExpanded ? renderExpandedItem() : renderCollapsedItem()}
    </Box>
  );
});

SidebarMenuItem.displayName = 'SidebarMenuItem';

export default SidebarMenuItem;
